import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { OrderImageTypes } from '@hero/schema';
import { orderImageTypesSchema } from '@hero/schema';
import { FileUploaderMulti, SelectAuto, type SelectAutoOption } from '@hero/web-components';
import { CheckIcon } from '@hero/web-icons';
import { cn } from '@hero/web-lib';
import { Input } from '../../../../ui/Input';
import { Label } from '../../../../ui/Label';

const IMAGE_ONLY_ACCEPT = {
  'image/jpeg': [],
  'image/png': [],
  'image/webp': [],
};

export interface OrderImageUploaderProps {
  files: File[];
  onFilesChange: (files: File[]) => void;
  /** Current image type. Locked when `onImageTypeChange` is omitted. */
  imageType: OrderImageTypes;
  /** When provided, renders a type selector. */
  onImageTypeChange?: (type: OrderImageTypes) => void;
  /** POD / document reference. Synced to `images[].customRef` on save. */
  podRef: string;
  onPodRefChange: (podRef: string) => void;
  /**
   * The order's current `podRef` (read-only display).
   * Used to show whether the order already has a POD ref.
   */
  orderPodRef?: string;
  /**
   * When true, the entered ref is also written to `order.podRef` on save.
   * Defaults should be true when the order has no podRef yet.
   */
  applyAsOrderPodRef: boolean;
  onApplyAsOrderPodRefChange: (apply: boolean) => void;
  /**
   * Show the ref field. Defaults to true when the type is a proof doc
   * (`proof_of_delivery_doc` / `proof_of_pickup_doc`).
   */
  showPodRef?: boolean;
  /** Mark the ref field as required (visual + parent validation). */
  podRefRequired?: boolean;
  className?: string;
}

function isProofDocType(type: OrderImageTypes): boolean {
  return type === 'proof_of_delivery_doc' || type === 'proof_of_pickup_doc';
}

/**
 * Shared multi-image picker for order photos.
 * Used by status update and the drawer Photos "Add image" flow.
 * Product language uses `podRef`; the API still stores per-image `customRef`.
 */
export const OrderImageUploader: React.FC<OrderImageUploaderProps> = ({
  files,
  onFilesChange,
  imageType,
  onImageTypeChange,
  podRef,
  onPodRefChange,
  orderPodRef,
  applyAsOrderPodRef,
  onApplyAsOrderPodRefChange,
  showPodRef,
  podRefRequired = false,
  className,
}) => {
  const { t } = useTranslation('m-order');

  const shouldShowPodRef = showPodRef ?? isProofDocType(imageType);
  const existingOrderPodRef = orderPodRef?.trim() || '';
  const hasOrderPodRef = existingOrderPodRef.length > 0;

  const typeOptions: SelectAutoOption[] = useMemo(
    () =>
      orderImageTypesSchema.options.map(type => ({
        value: type,
        label: t(`image_types.${type}`),
      })),
    [t],
  );

  const imageLabel =
    onImageTypeChange == null ? t(`image_types.${imageType}`) : t('form.image_type.title');

  return (
    <div className={cn('flex flex-col gap-4', className)}>
      {onImageTypeChange ? (
        <div className="flex flex-col gap-2">
          <Label>{imageLabel}</Label>
          <SelectAuto
            options={typeOptions}
            value={imageType}
            onChange={value => {
              if (value) onImageTypeChange(value as OrderImageTypes);
            }}
            placeholder={t('form.image_type.placeholder')}
          />
        </div>
      ) : (
        <Label>{imageLabel}</Label>
      )}

      <FileUploaderMulti
        files={files}
        onChange={items => {
          onFilesChange(items.filter((item): item is File => item instanceof File));
        }}
        acceptedFileTypes={IMAGE_ONLY_ACCEPT}
      />

      {shouldShowPodRef && (
        <div className="flex flex-col gap-3">
          <div
            className={cn(
              'flex items-center gap-2 rounded-lg border px-3 py-2.5 text-sm',
              hasOrderPodRef
                ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                : 'border-gray-200 bg-gray-50 text-gray-600',
            )}>
            {hasOrderPodRef ? (
              <>
                <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-emerald-500 text-white">
                  <CheckIcon className="size-3" />
                </span>
                <span className="min-w-0">
                  <span className="font-medium">{t('form.pod_ref.order_current')}: </span>
                  <span className="font-semibold tabular-nums">{existingOrderPodRef}</span>
                </span>
              </>
            ) : (
              <span>{t('form.pod_ref.order_empty')}</span>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <Label>
              {t('form.pod_ref.title')}
              {podRefRequired ? ' *' : ''}
            </Label>
            <Input
              value={podRef}
              onChange={e => onPodRefChange(e.target.value)}
              placeholder={t('form.pod_ref.placeholder')}
            />
          </div>

          <label className="flex cursor-pointer items-start gap-2.5 text-sm text-gray-700">
            <input
              type="checkbox"
              className="mt-0.5 size-4 shrink-0 rounded border-gray-300 text-primary focus:ring-primary"
              checked={applyAsOrderPodRef}
              onChange={e => onApplyAsOrderPodRefChange(e.target.checked)}
            />
            <span>
              {hasOrderPodRef
                ? t('form.pod_ref.apply_update')
                : t('form.pod_ref.apply_set')}
            </span>
          </label>
        </div>
      )}
    </div>
  );
};
